/**
 * connectivityLog — window-level offline/online transition telemetry.
 *
 * WHY (the gap this closes): three call sites listen to the browser's
 * `online`/`offline` events — `OfflineIndicator`, `OnlineStatusIndicator`, and
 * React-Query's `onlineManager` — but every one of them uses the transition to
 * drive UI or query behaviour ONLY. None emit a telemetry event. For an
 * all-AI-triage fleet (D-23 Supabase SSoT), a rep silently dropping offline is
 * the single most useful signal to correlate against a burst of failed writes,
 * a stalled offline-queue flush, or an SL-auth failure — yet it left NO trace.
 *
 * This installs a dedicated, side-effect-only pair of listeners that route the
 * transition to `scout_activity_log` (the same durable sink the error handlers
 * and the offline-queue safety-net use). It is orthogonal to the UI listeners
 * (which stay) — this one exists purely for observability.
 *
 * NOT an error path: going offline is normal operation, so these are logged at
 * event level `info`, never as `captured_error`. Genuine errors keep flowing
 * through `installGlobalErrorHandlers` / Sentry.
 *
 * Events emitted:
 *  - went_offline: { effective_type }
 *  - back_online:  { effective_type, offline_ms }  (offline_ms = time spent
 *                   offline, so triage can tell a 2s blip from a 20-min tunnel)
 *
 * Never throws. Idempotent. SSR-safe (no-ops without `window`).
 */

import { logScoutEvent } from './scoutLog'

/** `navigator.connection.effectiveType` where the Network Information API exists. */
function readEffectiveType(): string | null {
  try {
    const nav = navigator as Navigator & { connection?: { effectiveType?: string } }
    return typeof nav.connection?.effectiveType === 'string' ? nav.connection.effectiveType : null
  } catch {
    return null
  }
}

export function installConnectivityLogging(): void {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return
  const w = window as Window & { __SCOUT_CONNECTIVITY_LOGGING__?: boolean }
  if (w.__SCOUT_CONNECTIVITY_LOGGING__) return
  w.__SCOUT_CONNECTIVITY_LOGGING__ = true

  // Timestamp of the last offline transition, so back_online can report how long
  // the device was actually offline. Null while online.
  let offlineSince: number | null = null

  window.addEventListener('offline', () => {
    try {
      offlineSince = Date.now()
      logScoutEvent('went_offline', { effective_type: readEffectiveType() })
    } catch {
      // Telemetry must never throw from a connectivity handler.
    }
  })

  window.addEventListener('online', () => {
    try {
      const offlineMs = offlineSince != null ? Date.now() - offlineSince : null
      offlineSince = null
      logScoutEvent('back_online', { effective_type: readEffectiveType(), offline_ms: offlineMs })
    } catch {
      // Telemetry must never throw from a connectivity handler.
    }
  })
}
